import { useRouter } from 'next/router'
import * as R from 'ramda'
import React, { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { ThemeContext } from 'styled-components'
import URI from 'urijs'

import ErrorInfo from '../../components/ErrorInfo'
import { Col, Row } from '../../components/Layout/FlexGrid'
import MaxWidth from '../../components/Layout/MaxWidth'
import NextSeo from '../../components/NextSeo'
import Pagination from '../../components/Pagination'
import ScrollTo from '../../components/ScrollTo'
import { ProjectListingSkeleton } from '../../components/Skeleton'
import { ERROR_ICON_TYPE } from '../../constants'
import {
  AppviewNavigation,
  InstantSearchOverlay
} from '../../containers/Appview'
import Button from '../../containers/Button'
import {
  InstantSearchComponent,
  InstantSearchWrapper
} from '../../containers/InstantSearch'
import CtaCard from '../../containers/PersonalFundraising/CtaCard'
import ProjectCard from '../../containers/ProjectCard'
import { usePrev, useTracking } from '../../hooks'
import { useTranslation } from '../../i18n'
import { openNativeInAppBrowserRequest } from '../../redux/app/actions'
import { getCategoryDisplayNames } from '../../redux/category/selectors'
import {
  getDonationStatsRequest,
  instantSearchRequest
} from '../../redux/project/actions'
import { firePageView } from '../../redux/tracking/actions'
import { scrollToTop } from '../../utils/commonUtils'
import { getFontSize } from '../../utils/cssUtils'
import {
  discoverClickContactUs,
  discoverClickFaq,
  discoverClickSearchFundraisingEventBtn,
  discoverSearchCompleted,
  emptyResultView
} from '../../utils/dataTracker'
import mq from '../../utils/mediaQuery'

const Main = styled(MaxWidth)`
  position: relative;
  z-index: 0;
`
const StyledErrorInfo = styled(ErrorInfo)`
  margin: 47px auto;
`
const ListingMessage = styled.div`
  border-radius: 4px;
  box-shadow: 0 0 23px 0 rgba(0, 0, 0, 0.09);
  padding: 24px;
  margin-bottom: 16px;
`
const ListTitle = styled.div`
  font-size: ${getFontSize('title')};
  font-weight: 600;
  line-height: 1.4;
`
const ListContent = styled.p`
  font-size: ${getFontSize('body2')};
  line-height: 1.87;
  letter-spacing: 1.07px;
`
const Wrapper = styled.div`
  width: 100%;
  margin-bottom: 40px;
  position: relative;
  padding-top: 24px;
`
const StyledCtaCard = styled(CtaCard)`
  margin-bottom: 16px;
`
const PaginationWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  ${mq.tablet} {
    margin-bottom: 32px;
  }
  ${mq.mobile} {
    justify-content: center;
    margin-bottom: 16px;
  }
`

const FundraisingEventCtaCard = () => {
  const { t } = useTranslation('common')

  const router = useRouter()

  const { color } = useContext(ThemeContext)

  const track = useTracking()

  const [shouldScroll, setShouldScroll] = useState(false)

  const handleClick = () => {
    setShouldScroll(true)
    track(discoverClickSearchFundraisingEventBtn())
  }

  const handleStop = () => {
    setShouldScroll(false)
    const uri =
      '/discover?page=1&sort=newest&typeFilter=supportsFundraisingEvent'
    if (router.asPath !== uri) {
      router.push(uri, uri)
    }
  }

  return (
    <>
      <StyledCtaCard
        button={
          <Button
            type='button'
            height={48}
            bgColor={color.lightYellow}
            textColor={color.theme}
            onClick={handleClick}
          >
            {t(
              'personalFundraising.ctaButton.choosePersonalFundraisingProjectNow'
            )}
          </Button>
        }
      />
      <ScrollTo shouldScroll={shouldScroll} onStop={handleStop} scrollTo={0} />
    </>
  )
}

const sortings = ['newest', 'hottest']

const Discover = React.memo(() => {
  const limit = 12

  const dispatch = useDispatch()

  const track = useTracking()

  const router = useRouter()

  const { t } = useTranslation('common')

  const { query } = router

  let { page, sort, q: keyword, categories, typeFilter } = query

  page = parseInt(page)

  categories = !R.isNil(categories) ? R.split(',', categories) : []

  const categoryNames = useSelector(
    state => getCategoryDisplayNames(state, categories),
    R.equals
  )

  const [
    isInitialRender,
    isAppview,
    isWebview,
    latestProjectsNos,
    isFetching,
    total
  ] = useSelector(
    R.paths([
      ['appState', 'isInitialRender'],
      ['appState', 'isAppview'],
      ['appState', 'isWebview'],
      ['projectState', 'result', 'projects'],
      ['projectState', 'fetching', 'instantSearch'],
      ['projectState', 'total']
    ]),
    R.equals
  )

  const prevTotal = usePrev(total)

  const pushRoute = data => {
    const query = URI.buildQuery(data)
    const uri = URI.build({
      path: `/discover`,
      query
    })
    router.push(uri, uri)
  }

  const handlePageChange = page => {
    scrollToTop()
    pushRoute({
      page,
      sort,
      q: keyword,
      categories: R.isEmpty(categories) ? undefined : R.join(',', categories),
      typeFilter
    })
  }

  const handleListClickFaq = e => {
    if (isWebview) {
      e.preventDefault()
      dispatch(openNativeInAppBrowserRequest(e.currentTarget.href))
    }
    track(discoverClickFaq())
  }

  const handleListClickContact = e => {
    const { target } = e

    const isContactLink = R.allPass([
      R.propSatisfies(href => R.test(/^mailto:/, href), 'href'),
      R.propSatisfies(tagName => R.equals('a', R.toLower(tagName)), 'tagName')
    ])(target)

    if (isContactLink) {
      track(discoverClickContactUs())
    }
  }

  useEffect(() => {
    if (isInitialRender) {
      if (R.isEmpty(latestProjectsNos)) {
        emptyResultView()
      } else {
        dispatch(getDonationStatsRequest(latestProjectsNos))
      }
    }

    dispatch(firePageView())

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (R.isNil(prevTotal) && !R.isNil(total)) {
      track(
        discoverSearchCompleted(
          total,
          page,
          keyword,
          categoryNames,
          categories,
          typeFilter,
          sort
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total])

  return R.isNil(latestProjectsNos) ? null : (
    <Wrapper>
      <NextSeo
        title={t('seo.discoverPage.ogTitle')}
        description={t('seo.discoverPage.ogDescription')}
      />
      {isAppview && <InstantSearchOverlay />}
      <Main>
        <Row gutter={24}>
          <Col xs={24} md={24} xl={16}>
            {!isAppview && (
              <InstantSearchWrapper>
                <InstantSearchComponent
                  textSearch
                  selectSearch
                  resetBtn
                  sorter
                />
              </InstantSearchWrapper>
            )}
            {isFetching ? (
              <ProjectListingSkeleton count={limit} />
            ) : (
              <Row
                gutter={[24, 24]}
                css={`
                  margin-bottom: 24px;
                `}
              >
                {R.isEmpty(latestProjectsNos) ? (
                  <StyledErrorInfo
                    type={ERROR_ICON_TYPE.NO_RESULT}
                    title={t('listing.noRelatedProject')}
                  />
                ) : (
                  R.addIndex(R.map)(
                    (projectNo, i) => (
                      <Col xs={24} md={24} xl={24} key={projectNo}>
                        <ProjectCard
                          listView
                          projectNo={projectNo}
                          index={i}
                          total={total}
                          page={page}
                          searchText={keyword}
                          categoryNames={categoryNames}
                          categoryNos={categories}
                          typeFilter={typeFilter}
                          sort={sort}
                        />
                      </Col>
                    ),
                    latestProjectsNos
                  )
                )}
              </Row>
            )}
            <PaginationWrapper>
              <Pagination
                defaultCurrent={1}
                current={page}
                pageSize={limit}
                total={total}
                onChange={handlePageChange}
                showLess={isAppview}
              />
            </PaginationWrapper>
          </Col>
          <Col xs={24} md={24} xl={8}>
            <FundraisingEventCtaCard />
            <ListingMessage>
              <ListTitle>{t('listing.messageTitle')}</ListTitle>
              <ListContent>
                {t('listing.messageContent1')}
                <a
                  target='_blank'
                  rel='noreferrer'
                  href={process.env.HK01_FAQ_URL}
                  onClick={handleListClickFaq}
                >
                  {t('listing.messageContentLink')}
                </a>
                {t('listing.messageContent2')}
              </ListContent>
            </ListingMessage>
            <ListingMessage>
              <ListTitle>{t('listing.qnaTitle')}</ListTitle>
              <ListContent
                onClick={handleListClickContact}
                dangerouslySetInnerHTML={{
                  __html: t('listing.qnaContent')
                }}
              />
            </ListingMessage>
          </Col>
        </Row>
      </Main>
    </Wrapper>
  )
})

Discover.getInitialProps = async ({ query, store }) => {
  let { q, page, sort, categories, typeFilter } = query
  page = page || 1
  sort = sort || sortings[0]
  typeFilter = typeFilter === 'all' ? undefined : typeFilter
  categories = !R.isNil(categories) ? R.split(',', categories) : []
  const limit = 12
  const orgId = undefined
  const ignoreFeatured = false
  store.dispatch(
    instantSearchRequest(
      page,
      limit,
      sort,
      q,
      categories,
      orgId,
      ignoreFeatured,
      typeFilter
    )
  )

  return {}
}

Discover.renderPersistentLayout = () => {
  return {
    appviewHeader: () => null,
    appviewNavigation: () => <AppviewNavigation />
  }
}

export default Discover
